/**
 * Нэг аялалын дэлгэрэнгүй — `/purchasing/:id`.
 *
 * Дөрвөн асуултад дараалан хариулна:
 *   1. Цуглуулагч нийлүүлэгчид хэдийг өгсөн бэ (зарласан ↔ мөрүүд)
 *   2. Тэр гутлууд одоо хаана байна вэ (тулгалт)
 *   3. Хэзээ, ямар байдлаар бүртгэсэн бэ (суулт бүр зурагтай)
 *   4. Ямар загвар, размер орсон бэ (задаргаа)
 *
 * ⚠️ Задаргааг `breakdown`-оос авна, `items`-ээс БИШ: сүүлийнх нь
 *    100 мөрөөр тасардаг тул нэг аялалд 200-300 гутал байхад тоо
 *    чимээгүй багасаж харагдана.
 *
 * ⚠️ ₽ ба ₮ дүнгүүд `COST_ROLES`-оор хаагдана.
 */

import { formatDate, formatDateTime, formatMnt, formatQty, formatRub } from '@boots/domain';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { PhotoPanel } from '../components/photos';
import { PurchaseCosting } from '../components/purchase-costing';
import { PurchaseDefects } from '../components/purchase-defects';
import { Section } from '../components/section';
import { Button, Empty, ErrorNote, Spinner, Table, Td, Th } from '../components/ui';
import { api, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  COST_ROLES,
  GENDER_LABEL,
  PURCHASE_STATUS_LABEL,
  type PurchaseClose,
  type PurchaseDetail,
  type PurchaseItemRow,
  type PurchaseReconciliation,
} from '../lib/types';

/** Нэг хүсэлтэд татах гутлын тоо — сервэрийн дээд хязгаар. */
const ITEM_PAGE = 100;

export function PurchaseDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const showCost = can(...COST_ROLES);

  const trip = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => api<Envelope<PurchaseDetail>>(`/purchases/${id}`),
  });

  if (trip.isPending) return <Spinner />;
  if (trip.error) return <ErrorNote>{(trip.error as Error).message}</ErrorNote>;

  const p = trip.data.data;
  const place = [p.supplier.region].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col gap-3">
      <Link to="/purchasing" className="text-sm text-accent-700 hover:underline">
        ← Аялал руу
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="num text-xl font-semibold tracking-tight">{p.code ?? 'Дугаар олгогдоогүй'}</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            {formatDate(p.purchasedAt)} · {p.supplier.name}
            {place ? ` · ${place}` : ''} · {p.collector?.fullName ?? '—'}
          </p>
        </div>
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-hair/60 px-[11px] py-1 text-xs text-ink-2">
            {PURCHASE_STATUS_LABEL[p.status]}
          </span>
          {p.close.closedAt ? (
            <span className="rounded-full bg-accent-100 px-[11px] py-1 text-xs text-accent-700">
              Дууссан · {formatDate(p.close.closedAt)}
            </span>
          ) : (
            <span className="rounded-full bg-warn-bg px-[11px] py-1 text-xs text-warn-ink">
              Дуусгаагүй
            </span>
          )}
        </span>
      </div>

      <Settlement close={p.close} currency={p.currency} showCost={showCost} />

      {showCost ? <PurchaseCosting p={p} /> : null}

      <Section
        title="Хаана байна"
        note={`${formatQty(p.totals.items)} ш бүртгэгдсэн`}
      >
        <Reconciliation r={p.reconciliation} />
      </Section>

      {/*
        Орост цуглуулагч гутлыг тав таваар нь зурагладаг (шийдвэр
        2026-09-22) — аяллын зураг нь оффисын хувьд хайрцаг задлахаас
        ӨМНӨХ цорын ганц нотолгоо. Утсанд байгаа зургийг оффис
        харахгүй бол зарласан тоог хянах аргагүй болно.
      */}
      <PhotoPanel base="purchases" ownerId={id} title="Аяллын зураг" />

      <PurchaseDefects purchaseId={id} />

      <Registrations purchaseId={id} showCost={showCost} />

      <Section title="Загвар, размер" note={p.itemsTruncated ? 'Бүх мөрөөс тооцсон' : undefined}>
        <Breakdown p={p} showCost={showCost} />
      </Section>
    </div>
  );
}

// ------------------------------------------------------------ Тооцоо
/**
 * Цуглуулагчийн ЗАРЛАСАН нийлбэр ↔ гутлын мөрүүд.
 *
 * ⚠️ Хоёр тоог нэгтгэхгүй. Зөрүү нь дутуу бүртгэсэн гутлыг олох цорын
 *    ганц дохио — «зөв» тоог сонгоод нөгөөг нь нуувал тэр дохио үхнэ.
 *
 * ⚠️ ₮ талд зөрүү БАЙХГҮЙ: гутлын мөрүүд рублиэр үүсдэг тул зарласан
 *    ₮ дүнд харгалзах эсрэг тал байхгүй. Тэр нь тусдаа бодит төлбөр,
 *    ₽-ийн хөрвүүлэг биш.
 */
function Settlement({
  close,
  currency,
  showCost,
}: {
  close: PurchaseClose;
  currency: string;
  showCost: boolean;
}) {
  if (!showCost) {
    return (
      <Section title="Нийлүүлэгчтэй хийсэн тооцоо">
        <Empty>Дүнгийн мэдээлэл таны эрхэд харагдахгүй</Empty>
      </Section>
    );
  }

  if (!close.closedAt) {
    return (
      <Section title="Нийлүүлэгчтэй хийсэн тооцоо" note="утаснаас дуусгана">
        <p className="text-meta text-ink-2">
          Цуглуулагч аялалаа хараахан дуусгаагүй — зарласан нийлбэр алга.
          Гутлын мөрүүдээс: {formatRub(close.items.totalRub)} ({currency}), үүнээс{' '}
          {formatRub(close.items.creditRub)} зээл.
        </p>
      </Section>
    );
  }

  const short = close.variance.totalRub.startsWith('-');

  return (
    <Section
      title="Нийлүүлэгчтэй хийсэн тооцоо"
      subtitle={close.closedBy ? close.closedBy.fullName : undefined}
      note={`дуусгасан ${formatDateTime(close.closedAt)}`}
    >
      <Table>
        <thead>
          <tr>
            <Th>Юу</Th>
            <Th num>Зарласан</Th>
            <Th num>Гутлын мөрүүд</Th>
            <Th num>Зөрүү</Th>
          </tr>
        </thead>
        <tbody>
          {/* Бөөн бүртгэл: агуулах үүсгэсэн гутал ↔ цуглуулагчийн зарласан */}
          <tr>
            <Td>Ширхэг</Td>
            <Td num>{formatQty(close.declared.count)}</Td>
            <Td num>{formatQty(close.items.count)}</Td>
            <Td num className={close.items.count !== close.declared.count ? 'text-alert-ink' : ''}>
              {formatQty(close.items.count - close.declared.count)}
            </Td>
          </tr>
          <MoneyRow
            label="Нийт ₽"
            declared={close.declared.totalRub}
            items={close.items.totalRub}
            variance={close.variance.totalRub}
          />
          <MoneyRow
            label="Үүнээс зээл ₽"
            declared={close.declared.creditRub}
            items={close.items.creditRub}
            variance={close.variance.creditRub}
          />
          <tr className="border-t border-hair">
            <Td>Нийт ₮</Td>
            <Td num>{formatMnt(close.declared.totalMnt)}</Td>
            <Td num>—</Td>
            <Td num>—</Td>
          </tr>
          <tr className="border-t border-hair">
            <Td>Үүнээс зээл ₮</Td>
            <Td num>{formatMnt(close.declared.creditMnt)}</Td>
            <Td num>—</Td>
            <Td num>—</Td>
          </tr>
          {/* Зээлээ хэзээ төлөхөөр тохирсон — цуглуулагч Оросод заасан */}
          {close.creditDueDate ? (
            <tr className="border-t border-hair">
              <Td>Зээл төлөх хугацаа</Td>
              <Td num>{formatDate(close.creditDueDate)}</Td>
              <Td num>—</Td>
              <Td num className={new Date(close.creditDueDate) < new Date() ? 'text-alert-ink' : ''}>
                {new Date(close.creditDueDate) < new Date() ? 'хэтэрсэн' : '—'}
              </Td>
            </tr>
          ) : null}
        </tbody>
      </Table>

      <p className="text-label text-ink-3">
        ₮ дүн нь ₽-ийн хөрвүүлэг БИШ — тусдаа бодит төлбөр, тиймээс гутлын
        мөрүүдэд харгалзах эсрэг тал байхгүй.
      </p>

      {isZero(close.variance.totalRub) ? null : (
        <p className="text-meta text-alert-ink">
          {short
            ? `Мөрүүдийн нийлбэр ${formatRub(abs(close.variance.totalRub))}-аар их — зарласан дүн эсвэл гутлын үнэ буруу шивэгдсэн байж болзошгүй.`
            : `Зарласан нь ${formatRub(close.variance.totalRub)}-аар их — гутал дутуу бүртгэгдсэн байж болзошгүй.`}
        </p>
      )}
    </Section>
  );
}

/**
 * ⚠️ Дүнг ЭНД БОДОХГҮЙ — зөвхөн харуулна. Зөрүүг сервер Decimal-аар
 *    бодоод string болгож өгсөн; `Number` рүү хөрвүүлээд `Math.abs`
 *    хийвэл мөнгө хөвөгч цэг рүү орно (CLAUDE.md №3).
 */
const isZero = (v: string) => /^-?0+(\.0+)?$/.test(v);
const abs = (v: string) => (v.startsWith('-') ? v.slice(1) : v);

function MoneyRow({
  label,
  declared,
  items,
  variance,
}: {
  label: string;
  declared: string;
  items: string;
  variance: string;
}) {
  const zero = isZero(variance);
  return (
    <tr className="border-t border-hair">
      <Td>{label}</Td>
      <Td num>{formatRub(declared)}</Td>
      <Td num>{formatRub(items)}</Td>
      <Td num className={zero ? 'text-ink-3' : 'text-alert-ink'}>
        {zero
          ? formatRub('0')
          : `${variance.startsWith('-') ? '−' : '+'}${formatRub(abs(variance))}`}
      </Td>
    </tr>
  );
}

// ------------------------------------------------------------ Бүртгэлийн суулт
/**
 * «Хэзээ, ямар байдлаар бүртгэсэн бэ» — суулт бүр зурагтай.
 *
 * СУУЛТ гэж юу вэ: дараалсан, ижил загвар · ижил нэгж үнэ · ижил
 * төлбөрийн хэлбэртэй, хоорондоо 30 минутаас бага зайтай мөрүүд.
 * Олноор бүртгэх урсгал (`BulkStep`) яг ийм цуваа үүсгэдэг: нэг
 * загвар, нэг үнэ, зураг тус бүрд нь, хормын зайтай.
 *
 * ⚠️ Бүлэглэлт нь БОДИТ МӨРҮҮДЭЭС гарна, тусад нь хадгалагддаг «багц»
 *    гэж байхгүй. `items.batch`-ийн `operationId` нь тохирохгүй: тэр нь
 *    outbox тэр агшинд ямар мөр бэлэн байснаас хамаардаг — сүлжээ
 *    тасарсан газар нэг суулт хоёр багц болж хуваагддаг.
 *
 * ⚠️ Мөрүүд `id`-ээр (UUID v7 = бүртгэсэн дараалал) ирнэ. `collectedAt`
 *    зөвхөн цаг заана; ижил секундэд бүртгэгдсэн мөрүүдийг тэр
 *    ялгахгүй.
 */
const RUN_GAP_MS = 30 * 60_000;

interface Run {
  items: PurchaseItemRow[];
}

function groupRuns(rows: PurchaseItemRow[]): Run[] {
  const runs: Run[] = [];
  for (const it of rows) {
    const last = runs.at(-1);
    const head = last?.items[0];
    const tail = last?.items.at(-1);
    const same =
      last !== undefined &&
      head !== undefined &&
      tail !== undefined &&
      head.model.id === it.model.id &&
      head.purchaseAmount === it.purchaseAmount &&
      head.isCredit === it.isCredit &&
      new Date(it.collectedAt).getTime() - new Date(tail.collectedAt).getTime() <= RUN_GAP_MS;
    if (same) last.items.push(it);
    else runs.push({ items: [it] });
  }
  return runs;
}

function Registrations({ purchaseId, showCost }: { purchaseId: string; showCost: boolean }) {
  const items = useInfiniteQuery({
    queryKey: ['purchase-items', purchaseId],
    queryFn: ({ pageParam }) =>
      api<PagedEnvelope<PurchaseItemRow>>(
        `/purchases/${purchaseId}/items?limit=${ITEM_PAGE}${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.meta.nextCursor,
  });

  const rows = items.data?.pages.flatMap((x) => x.data) ?? [];
  const total = items.data?.pages[0]?.meta.total ?? 0;
  const runs = groupRuns(rows);

  return (
    <Section
      title="Бүртгэл"
      subtitle={`${formatQty(runs.length)} суулт`}
      note={
        rows.length < total
          ? `${formatQty(rows.length)} / ${formatQty(total)} гутал харагдаж байна`
          : `${formatQty(total)} гутал`
      }
    >
      {items.isPending ? <Spinner /> : null}
      {items.error ? <ErrorNote>{(items.error as Error).message}</ErrorNote> : null}
      {!items.isPending && rows.length === 0 ? <Empty>Энэ аялалд бараа бүртгэгдээгүй</Empty> : null}

      <ul className="flex flex-col gap-2.5">
        {runs.map((run) => (
          <RunCard key={run.items[0]!.id} run={run} showCost={showCost} />
        ))}
      </ul>

      {items.hasNextPage ? (
        <Button
          variant="ghost"
          disabled={items.isFetchingNextPage}
          onClick={() => void items.fetchNextPage()}
        >
          {items.isFetchingNextPage ? 'Ачаалж байна…' : 'Дараагийн 100 гутал'}
        </Button>
      ) : null}
    </Section>
  );
}

function RunCard({ run, showCost }: { run: Run; showCost: boolean }) {
  const head = run.items[0]!;
  const bulk = run.items.length > 1;

  return (
    <li className="flex flex-col gap-2.5 rounded-card border border-hair px-3.5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="num whitespace-nowrap">{formatDateTime(head.collectedAt)}</span>
        <span className="rounded-full bg-accent-100 px-2.5 py-[3px] text-label text-accent-700">
          {bulk ? 'Олноор' : 'Ганцаар'}
        </span>
        <span className="font-medium text-pretty">
          {head.model.brand} {head.model.name}
        </span>
        <span className="text-meta text-ink-2">{GENDER_LABEL[head.model.gender]}</span>
        {head.isCredit ? (
          <span className="rounded-full bg-alert-bg px-2.5 py-[3px] text-label text-alert-ink">
            зээлээр
          </span>
        ) : null}
        <span className="num ml-auto whitespace-nowrap">
          {formatQty(run.items.length)} ш
          {showCost ? ` · ${formatRub(head.purchaseAmount)}/ш` : ''}
        </span>
      </div>

      {/* Зураг нь баталгааны нотолгоо — бүртгэлийг НҮДЭЭР шалгах цорын
          ганц зам. Дарвал гутлын дэлгэрэнгүй рүү орно. */}
      <ul className="flex flex-wrap gap-2">
        {run.items.map((it) => (
          <li key={it.id}>
            <Link
              to={`/items/${it.id}`}
              title={`${it.serial ?? it.tempCode ?? 'Шошгогүй'}${
                it.size !== null ? ` · размер ${it.size}` : ' · размергүй'
              }`}
              className="flex size-[74px] items-center justify-center overflow-hidden rounded-well
                border border-hair bg-well transition hover:border-accent-300"
            >
              {it.photo?.thumbUrl ?? it.photo?.mediumUrl ?? it.photo?.originalUrl ? (
                <img
                  src={it.photo?.thumbUrl ?? it.photo?.mediumUrl ?? it.photo?.originalUrl ?? ''}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover"
                />
              ) : (
                <span className="num px-1 text-center text-[10.5px] text-ink-3">
                  {it.size !== null ? it.size : 'зураггүй'}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

// ------------------------------------------------------------ Задаргаа
function Breakdown({ p, showCost }: { p: PurchaseDetail; showCost: boolean }) {
  if (p.breakdown.length === 0) return <Empty>Энэ аялалд бараа бүртгэгдээгүй</Empty>;

  return (
    <Table>
      <thead>
        <tr>
          <Th>Загвар</Th>
          <Th>Хүйс</Th>
          <Th num>Размер</Th>
          <Th num>Тоо</Th>
          {showCost ? <Th num>Өртөг</Th> : null}
        </tr>
      </thead>
      <tbody>
        {p.breakdown.map((b) => (
          <tr key={`${b.modelId}-${b.size}`} className="border-t border-hair">
            <Td>
              <span className="flex min-w-0 flex-col">
                <span className="text-pretty">
                  {b.model ? `${b.model.brand} ${b.model.name}` : 'Загвар устсан'}
                </span>
                <span className="num text-label text-ink-3">{b.model?.code ?? b.modelId}</span>
              </span>
            </Td>
            <Td>
              <span className="text-ink-2">{b.model ? GENDER_LABEL[b.model.gender] : '—'}</span>
            </Td>
            <Td num>{b.size}</Td>
            <Td num>{formatQty(b.count)}</Td>
            {showCost ? <Td num>{formatMnt(b.landedCostMnt)}</Td> : null}
          </tr>
        ))}
        <tr className="border-t border-hair font-medium">
          <Td>Нийт</Td>
          <Td>{null}</Td>
          <Td num>{null}</Td>
          <Td num>{formatQty(p.totals.items)}</Td>
          {showCost ? <Td num>{formatMnt(p.totals.landedCostMnt)}</Td> : null}
        </tr>
      </tbody>
    </Table>
  );
}

// ------------------------------------------------------------ Тулгалт
/**
 * «Энэ аялалаас хэд ирэх ёстой байсан, одоо хаана хэд байна».
 *
 * ⚠️ Байршлаар бүлэглэнэ, ТӨЛӨВӨӨР биш — `ItemStatus` нь байршил
 *    заадаггүй (CLAUDE.md №9). «Зарагдсан» гутал ч байршилтай:
 *    `CUSTOMER` төрлийн байршилд очдог.
 */
function Reconciliation({ r }: { r: PurchaseReconciliation }) {
  // Цуглуулагч урьдчилж тоо заагаагүй бол «дутуу» гэж уншиж болохгүй
  const short = r.expected > 0 ? r.expected - r.registered : 0;

  return (
    <div className="flex flex-col gap-2 rounded-well bg-well px-3.5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-meta">
        {r.expected > 0 ? <Fact label="Ирэх ёстой" value={formatQty(r.expected)} /> : null}
        <Fact label="Бүртгэгдсэн" value={formatQty(r.registered)} />
        <Fact
          label="Шошготой"
          value={formatQty(r.labeled)}
          alert={r.unlabeled > 0 ? `${formatQty(r.unlabeled)} шошгогүй` : undefined}
        />
        {r.unlocated > 0 ? (
          <Fact label="Байршилгүй" value={formatQty(r.unlocated)} alert="хүлээж аваагүй" />
        ) : null}
        {short > 0 ? (
          <span className="num rounded-full bg-alert-bg px-2.5 py-[3px] text-label text-alert-ink">
            {formatQty(short)} дутуу
          </span>
        ) : null}
      </div>

      {/* Хаана байгаа — салбар бүрээр */}
      <div className="flex flex-wrap gap-1.5">
        {r.byLocation.map((l) => (
          <span
            key={l.locationId}
            title={`${l.locationName ?? l.locationId}${l.storeName ? ` · ${l.storeName}` : ''}`}
            className={`inline-flex items-baseline gap-1.5 rounded-full px-2.5 py-[3px] text-label ${
              l.isSellable ? 'bg-accent-100 text-accent-700' : 'bg-panel text-ink-2'
            }`}
          >
            {l.storeName ?? l.locationName ?? '—'}
            <span className="num">{formatQty(l.count)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Fact({ label, value, alert }: { label: string; value: string; alert?: string }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-ink-2">{label}</span>
      <span className="num">{value}</span>
      {alert ? <span className="text-label text-alert-ink">{alert}</span> : null}
    </span>
  );
}
